//! The ruleset's exclusive *axes* — climate, thirst and the sea axis —
//! as wire-level unions. Split out of ruleset.ts (498 lines and growing
//! past the repo's 400-line rule); re-exported from ruleset.ts so every
//! existing `import { type Climate } from '@aod/protocol'` keeps working.

/** Map-generation climate (terrain-levels design D11). Biome weights,
 * snow line and ground palette derive from it; `temperate` (the default)
 * is today's behaviour. */
export type Climate = 'temperate' | 'arctic' | 'tropical' | 'desert';

/** Thirst (desert water carrying). `auto` (default) turns it on from AI
 * difficulty level 3 (normal) up; `on`/`off` force it for every player. It
 * rides in the ruleset so replays and multiplayer stay in sync. */
export type Thirst = 'auto' | 'on' | 'off';

/** The sea axis (sea-design §3 A1): `rim` (default) keeps today's coast
 * ring, `landlocked` walls the board in mountains, `open` adds a deep ocean
 * ring, `archipelago` is islands. Absent = 'rim'. */
export type Sea = 'rim' | 'landlocked' | 'open' | 'archipelago';

/** The sea frame the sim generated the board with (sea-design §3 A2): how
 * many rings of what surround the land. `rim` is `{1, 1, 0}` — today's
 * board — and the frame is published with the snapshot so the renderer
 * reads the real rings instead of guessing them from the tiles. */
export interface SeaFrame {
  /** Rings of deep ocean, from the board edge inward. */
  readonly oceanRings: number;
  /** Rings of shallow coast between the ocean and the land. */
  readonly coastRings: number;
  /** Rings of impassable mountain wall (landlocked), outermost first. */
  readonly wallRings: number;
}

/** The sea axis as the sim resolved it: the mode and the frame it built
 * the board with. */
export interface SeaState {
  readonly mode: Sea;
  readonly frame: SeaFrame;
}

/** Today's board: a rim board with no sea axis (the default). */
export const RIM_SEA: SeaState = {
  mode: 'rim',
  frame: { oceanRings: 1, coastRings: 1, wallRings: 0 },
};
